import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { MapPin, Grape, Building2, CalendarDays, Compass, Route as RouteIcon, MessageSquare, Camera, ChevronRight } from "lucide-react";
import AiBadge from "@/components/AiBadge";

const HUBS = [
  { key: "appellations", to: "/appellations", icon: MapPin,
    title_fr: "Appellations", title_en: "Appellations",
    desc_fr: "Toutes les AOC, IGP, DOC, DO… avec terroirs et cépages clés.", desc_en: "All AOCs, IGPs, DOCs, DOs… with terroirs and key grapes." },
  { key: "cepages", to: "/cepages", icon: Grape,
    title_fr: "Cépages", title_en: "Grapes",
    desc_fr: "L'atlas des cépages : origine, arômes, accords.", desc_en: "The grape atlas: origin, aromas, pairings." },
  { key: "producers", to: "/producers", icon: Building2,
    title_fr: "Producteurs & Domaines", title_en: "Producers & Estates",
    desc_fr: "Les domaines inscrits + découverte mondiale via Google Places.", desc_en: "Listed estates + worldwide discovery via Google Places.", ai: true },
  { key: "events", to: "/events", icon: CalendarDays,
    title_fr: "Événements", title_en: "Events",
    desc_fr: "Salons, foires aux vins, portes ouvertes, masterclasses.", desc_en: "Trade shows, wine fairs, open cellars, masterclasses." },
  { key: "nearby", to: "/nearby", icon: Compass,
    title_fr: "Around Me", title_en: "Around Me",
    desc_fr: "Producteurs, caveaux, bars à vin et restaurants à proximité.", desc_en: "Producers, cellars, wine bars and restaurants nearby.", ai: true },
  { key: "itineraries", to: "/itineraries", icon: RouteIcon,
    title_fr: "Routes & Itinéraires", title_en: "Routes & Itineraries",
    desc_fr: "Circuits œnotouristiques personnalisés par geoVINUM AI.", desc_en: "Wine-tourism routes personalised by geoVINUM AI.", ai: true },
  { key: "explorer", to: "/explorer", icon: Camera,
    title_fr: "Vision (photo)", title_en: "Vision (photo)",
    desc_fr: "Photographiez une bouteille, étiquette, vignoble — geoVINUM AI reconnaît.", desc_en: "Snap a bottle, label or vineyard — geoVINUM AI identifies.", ai: true },
  { key: "chat", to: "/chat", icon: MessageSquare,
    title_fr: "Ask geoVINUM AI", title_en: "Ask geoVINUM AI",
    desc_fr: "Posez n'importe quelle question vin. Comparez des cuvées, explorez un terroir.", desc_en: "Ask anything about wine. Compare cuvées, explore a terroir.", ai: true },
];

export default function DiscoverHub() {
  const { lang } = useT();
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="discover-hub">
      <span className="chip-pink inline-flex items-center gap-1.5">{lang === "fr" ? "Module" : "Module"} · Discover <AiBadge variant="icon" /></span>
      <h1 className="font-display text-5xl md:text-6xl text-[#1F3A5F] mt-4 leading-[1.05]">
        {lang === "fr" ? "Explorez le monde du vin" : "Explore the world of wine"}
      </h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        {lang === "fr"
          ? "Producteurs, appellations, cépages, terroirs, itinéraires et expériences — guidés par geoVINUM AI, à partir d'une photo, d'une question ou de votre position."
          : "Producers, appellations, grapes, terroirs, routes and experiences — guided by geoVINUM AI, from a photo, a question or your location."}
      </p>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {HUBS.map((h) => {
          const Icon = h.icon;
          return (
            <Link
              key={h.key}
              to={h.to}
              data-testid={`discover-hub-${h.key}`}
              className="group rounded-2xl border border-[#1F3A5F]/10 bg-white p-5 hover:border-[#E5276A] transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="p-2.5 rounded-xl bg-[#FCE4ED] text-[#E5276A]"><Icon className="w-5 h-5" /></span>
                {h.ai && <AiBadge variant="icon" />}
              </div>
              <h3 className="font-display text-lg text-[#1F3A5F] mt-3">{lang === "fr" ? h.title_fr : h.title_en}</h3>
              <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">{lang === "fr" ? h.desc_fr : h.desc_en}</p>
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#E5276A] opacity-0 group-hover:opacity-100 transition-opacity">
                {lang === "fr" ? "Ouvrir" : "Open"} <ChevronRight className="w-3 h-3" />
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
